import type { AttentionBannerKind } from "./attention-banner";
import type { AttentionComposerMode } from "./attention-composer-model";
import type { AttentionHeaderActionId } from "./attention-header";

/** The kind of message a send produced, for per-type send tracking. */
export type AttentionMessageType = "text" | "image" | "video" | "voice" | "gif";

/** Toolbar affordances that open a composer mode. */
export type AttentionToolbarAction = Exclude<AttentionComposerMode, "idle">;

/** Suggestion families the AI endpoint returns. */
export type AttentionSuggestionType = "default" | "specific" | "priority";

export type AttentionBannerToggleState = "collapsed" | "expanded";

/**
 * PII-safe prop builders: every builder returns ids, enums, booleans, lengths,
 * and durations only — never message text, names, GIF URLs, or suggestion content.
 */

export function attentionBannerProps(
  kind: AttentionBannerKind,
  teenId: string | null,
): Record<string, unknown> {
  return { banner_kind: kind, teen_id: teenId };
}

export function attentionBannerActionProps(
  kind: AttentionBannerKind,
  actionId: string,
  teenId: string | null,
): Record<string, unknown> {
  return { banner_kind: kind, action_id: actionId, teen_id: teenId };
}

export function attentionBannerToggleProps(
  kind: AttentionBannerKind,
  state: AttentionBannerToggleState,
  teenId: string | null,
): Record<string, unknown> {
  return { banner_kind: kind, state, teen_id: teenId };
}

export function attentionToolbarActionProps(
  action: AttentionToolbarAction,
  teenId: string | null,
): Record<string, unknown> {
  return { action, teen_id: teenId };
}

export function attentionMessageSendProps(
  messageType: AttentionMessageType,
  hasQuote: boolean,
  teenId: string | null,
): Record<string, unknown> {
  return { message_type: messageType, has_quote: hasQuote, teen_id: teenId };
}

export function attentionVoiceProps(
  durationMs: number,
  hasQuote: boolean,
  teenId: string | null,
): Record<string, unknown> {
  return { duration_ms: durationMs, has_quote: hasQuote, teen_id: teenId };
}

export function attentionSuggestionProps(
  suggestionType: AttentionSuggestionType | null,
  approach: string | null,
  teenId: string | null,
): Record<string, unknown> {
  return {
    suggestion_type: suggestionType,
    approach,
    teen_id: teenId,
  };
}

export function attentionHeaderActionProps(
  actionId: AttentionHeaderActionId,
  teenId: string | null,
): Record<string, unknown> {
  return { action_id: actionId, teen_id: teenId };
}
